import React, { useId } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";

import { useTranslation } from "../../context/Internationalization";
import { Button } from "../../shadcn/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "../../shadcn/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn/components/ui/select";

const PAGE_SIZE_ITEMS = [
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
];

// Page controls under a table. The page owns page state; `page` is 0-based.
// Without onRowsPerPageChange the page size is fixed and its picker is hidden.
const TablePagination = ({
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  disabled = false,
}) => {
  const { t = (key) => key } = useTranslation();
  const pageSizeLabelId = useId();
  const summaryId = useId();
  const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const shownPage = Math.min(page, lastPage);
  const from = total === 0 ? 0 : shownPage * rowsPerPage + 1;
  const to = total === 0 ? 0 : Math.min(total, (shownPage + 1) * rowsPerPage);
  const isFirstPage = disabled || shownPage <= 0;
  const isLastPage = disabled || total === 0 || shownPage >= lastPage;
  const rangeSummary =
    from + "-" + to + " " + t("table_pagination_of") + " " + total;

  // Laid out like the @shadcndesign section-footer-5 block: previous on the
  // left, the range (and page size) in the middle, next on the right.
  return (
    <Pagination
      data-slot="table-pagination"
      aria-label={t("pagination_navigation_label")}
      aria-describedby={summaryId}
      className="mx-0 w-full px-4 py-4"
    >
      <PaginationContent className="w-full flex-wrap justify-between gap-4">
        <PaginationItem className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t("pagination_first_page")}
            disabled={isFirstPage}
            onClick={() => onPageChange(0)}
          >
            <ChevronsLeftIcon aria-hidden="true" focusable="false" />
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-10 md:w-auto md:min-w-[112px]"
            aria-label={t("pagination_previous_page")}
            disabled={isFirstPage}
            onClick={() => onPageChange(Math.max(0, shownPage - 1))}
          >
            <ArrowLeftIcon aria-hidden="true" focusable="false" />
            <span aria-hidden="true" className="hidden md:block">
              {t("pagination_previous_page")}
            </span>
          </Button>
        </PaginationItem>

        <PaginationItem className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <p
            id={summaryId}
            className="m-0 text-center text-sm whitespace-nowrap text-muted-foreground tabular-nums"
            aria-live="polite"
            aria-atomic="true"
          >
            {rangeSummary}
          </p>
          {onRowsPerPageChange ? (
            <div className="flex items-center gap-3">
              <span
                id={pageSizeLabelId}
                className="text-sm text-muted-foreground"
              >
                {t("table_rows_per_page")}
              </span>
              <Select
                items={PAGE_SIZE_ITEMS}
                value={rowsPerPage}
                onValueChange={(nextRowsPerPage) =>
                  onRowsPerPageChange(Number(nextRowsPerPage))
                }
              >
                <SelectTrigger
                  aria-labelledby={pageSizeLabelId}
                  aria-describedby={summaryId}
                  className="w-[70px] shrink-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_ITEMS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </PaginationItem>

        <PaginationItem className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-10 md:w-auto md:min-w-[112px]"
            aria-label={t("pagination_next_page")}
            disabled={isLastPage}
            onClick={() => onPageChange(Math.min(lastPage, shownPage + 1))}
          >
            <span aria-hidden="true" className="hidden md:block">
              {t("pagination_next_page")}
            </span>
            <ArrowRightIcon aria-hidden="true" focusable="false" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t("pagination_last_page")}
            disabled={isLastPage}
            onClick={() => onPageChange(lastPage)}
          >
            <ChevronsRightIcon aria-hidden="true" focusable="false" />
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export default TablePagination;
